// Dono: B4 (redescoberta). /reencontro: sessão de Reencontro (mínimo LIMITS.serendipityMin elegíveis).
// §7.8 e F-W9: abaixo do mínimo diz quantos faltam; senão uma rodada de N cards (prefs.serendipityCount) ou 2 min.
// Manter (→, botão ou deslizar à direita) grava lastReviewedAt; esquecer (←, botão ou deslizar à esquerda) vai para a
// lixeira com "desfazer". Clique (ou Enter) no card abre o detalhe, onde dá para etiquetar e pôr em coleção.
import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { type Card, LIMITS } from "../../../../shared/types.ts";
import { IconArrowLeft, IconArrowRight } from "../detail/icons.tsx";
import { draw, eligible } from "../detail/rules.ts";
import { Caption, Frame, ImageRetry } from "../Grid.tsx";
import { cardText, isTypingTarget, KIND_LABEL } from "../lib.ts";
import { backTo, byId, deleteCard, failed, refocus, route, store } from "../state.ts";

const SESSION_MS = 120_000;
const EXIT_MS = 460;
const reduced = () => matchMedia("(prefers-reduced-motion: reduce)").matches;
const touch = () => matchMedia("(pointer: coarse)").matches;

type Run = { deck: Card[]; i: number; startedAt: number; kept: number; forgot: number; leaving: "left" | "right" | null };
type Phase = { at: "intro" } | ({ at: "run" } & Run) | { at: "end"; kept: number; forgot: number; timeUp: boolean };

const clock = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export function SerendipityPage() {
  const [phase, setPhase] = useState<Phase>({ at: "intro" });
  const [now, setNow] = useState(Date.now());
  const pool = eligible(store.cards.value, now);
  const n = store.prefs.value.serendipityCount;
  const missing = LIMITS.serendipityMin - pool.length;

  const start = () => {
    const t = Date.now();
    const ok = eligible(store.cards.value, t);
    setNow(t);
    if (ok.length < LIMITS.serendipityMin) return setPhase({ at: "intro" });
    setPhase({ at: "run", deck: draw(ok, n, t), i: 0, startedAt: t, kept: 0, forgot: 0, leaving: null });
    refocus(() => document.querySelector<HTMLElement>(".rd-face"));
  };

  // Relógio da rodada; no fim do tempo a rodada acaba (o card na tela fica sem decisão). Com o detalhe (ou outro
  // diálogo) aberto o relógio para: o tempo parado empurra o início da rodada.
  useEffect(() => {
    if (phase.at !== "run") return;
    let last = Date.now();
    const id = setInterval(() => {
      const t = Date.now();
      const paused = t - last;
      if (document.querySelector("dialog[open]")) setPhase((p) => (p.at === "run" ? { ...p, startedAt: p.startedAt + paused } : p));
      last = t;
      setNow(t);
    }, 1000);
    return () => clearInterval(id);
  }, [phase.at]);
  useEffect(() => {
    if (phase.at === "run" && now - phase.startedAt >= SESSION_MS && !phase.leaving) setPhase({ at: "end", kept: phase.kept, forgot: phase.forgot, timeUp: true });
  }, [now]);

  // Detalhe fechado: o card aberto pode ter saído (apagado lá dentro, fim da rodada); o foco vai ao card da vez ou ao título.
  const detailOpen = !!route.value.cardId;
  const wasOpen = useRef(detailOpen);
  useEffect(() => {
    if (wasOpen.current && !detailOpen) refocus(() => document.querySelector<HTMLElement>(".rd-face, #rd-story-title"));
    wasOpen.current = detailOpen;
  }, [detailOpen]);

  return (
    <main id="main" tabIndex={-1} class="page soon serendipity" data-phase={phase.at}>
      <header class="page-head">
        <h1>Reencontro</h1>
        {phase.at === "run" && (
          <p class="rd-count">
            {Math.min(phase.i + 1, phase.deck.length)} de {phase.deck.length}
            <span aria-hidden="true"> · </span>
            <span title="Tempo que falta na rodada">{clock(SESSION_MS - (now - phase.startedAt))}</span>
          </p>
        )}
      </header>
      <div class="rd-stage">
        <div class="rd-glow" aria-hidden="true" />
        {phase.at === "run" ? (
          <Session run={phase} now={now} set={setPhase} />
        ) : phase.at === "end" ? (
          <Story
            kicker={phase.timeUp ? "O tempo da rodada acabou" : "Rodada completa"}
            title="Rodada feita."
            line={`Você manteve ${phase.kept} e esqueceu ${phase.forgot}. ${phase.forgot ? `O que foi esquecido fica na lixeira por ${LIMITS.trashDays} dias.` : "Nada foi para a lixeira."}`}
          >
            {missing <= 0 ? (
              <button type="button" class="rd-cta" onClick={start}>
                Mais uma rodada
              </button>
            ) : (
              <p class="rd-meta">Para outra rodada {missing === 1 ? "falta 1 card elegível" : `faltam ${missing} cards elegíveis`}.</p>
            )}
            <a class="rd-cta is-quiet" href="/" onClick={backTo("/")}>
              Voltar para Tudo
            </a>
          </Story>
        ) : missing > 0 ? (
          <Story
            kicker="Reencontro"
            title="Ainda é cedo para um reencontro."
            line={`A rodada abre com ${LIMITS.serendipityMin} cards guardados há mais de uma semana. ${missing === 1 ? "Falta 1." : `Faltam ${missing}.`}`}
          >
            <div class="rd-meter" aria-hidden="true">
              <span style={`width:${Math.round((pool.length / LIMITS.serendipityMin) * 100)}%`} />
            </div>
            <p class="rd-meta">
              {pool.length} de {LIMITS.serendipityMin}. Cards privados e os que você manteve no último mês ficam de fora.
            </p>
            <a class="rd-cta is-quiet" href="/" onClick={backTo("/")}>
              Voltar para Tudo
            </a>
          </Story>
        ) : (
          <Story
            kicker="Reencontro"
            title="Coisas que você guardou e o tempo escondeu."
            line="Uma rodada curta com cards antigos. Mantenha o que ainda faz sentido; esqueça o que já cumpriu o papel (vai para a lixeira e dá para desfazer)."
          >
            <button type="button" class="rd-cta" onClick={start}>
              Começar a rodada
            </button>
            {touch() ? (
              <p class="rd-meta">{n} cards ou 2 minutos. Deslize para a direita para manter, para a esquerda para esquecer.</p>
            ) : (
              <p class="rd-meta">
                {n} cards ou 2 minutos · <kbd>→</kbd> manter · <kbd>←</kbd> esquecer
              </p>
            )}
          </Story>
        )}
      </div>
    </main>
  );
}

function Story({ kicker, title, line, children }: { kicker: string; title: string; line: string; children: ComponentChildren }) {
  return (
    <section class="rd-story" aria-labelledby="rd-story-title">
      <p class="rd-kicker">{kicker}</p>
      <h2 id="rd-story-title" tabIndex={-1}>
        {title}
      </h2>
      <p class="rd-line">{line}</p>
      <div class="rd-story-actions">{children}</div>
    </section>
  );
}

function Session({ run, now, set }: { run: Run; now: number; set: (p: Phase) => void }) {
  const card = run.deck[run.i];
  const c = card && byId.value.get(card.id);
  const drag = useRef<{ x: number; dx: number; id: number } | null>(null);
  const face = useRef<HTMLAnchorElement>(null);
  const runRef = useRef(run);
  runRef.current = run;

  const next = (r: Run) => {
    if (r.i + 1 >= r.deck.length) return set({ at: "end", kept: r.kept, forgot: r.forgot, timeUp: false });
    set({ at: "run", ...r, i: r.i + 1, leaving: null });
    refocus(() => document.querySelector<HTMLElement>(".rd-face"), { preventScroll: true });
  };
  const decide = (dir: "left" | "right") => {
    const r = runRef.current;
    const cur = r.deck[r.i];
    if (!cur || r.leaving) return;
    if (dir === "right") store.review(cur.id).catch(failed);
    else deleteCard(cur.id).catch(failed);
    const after: Run = { ...r, leaving: dir, kept: r.kept + (dir === "right" ? 1 : 0), forgot: r.forgot + (dir === "left" ? 1 : 0) };
    set({ at: "run", ...after });
    setTimeout(() => next(after), reduced() ? 0 : EXIT_MS);
  };

  // Card que sumiu no meio da rodada (apagado ou privado pelo detalhe): pula.
  useEffect(() => {
    if (card && (!c || c.deletedAt || c.isPrivate) && !run.leaving) next(run);
  }, [card?.id, !!c, !!c?.deletedAt, !!c?.isPrivate]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target) || document.querySelector("dialog[open]")) return;
      if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
      e.preventDefault();
      decide(e.key === "ArrowRight" ? "right" : "left");
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  if (!c) return null;
  const progress = Math.min(1, Math.max((now - run.startedAt) / SESSION_MS, run.i / run.deck.length));
  const ar = c.kind === "image" && c.width && c.height ? c.width / c.height : c.kind === "link" ? 1 : 0;
  const label = `${KIND_LABEL[c.kind]}: ${cardText(c).slice(0, 80)}`;

  // Deslizar: o card segue o dedo; passou de 90 px, decide. Até 6 px é clique (abre o detalhe).
  const onDown = (e: PointerEvent) => {
    if (e.button || run.leaving) return;
    drag.current = { x: e.clientX, dx: 0, id: e.pointerId };
  };
  const onMove = (e: PointerEvent) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    d.dx = e.clientX - d.x;
    if (Math.abs(d.dx) <= 6) return;
    face.current?.setPointerCapture(e.pointerId);
    face.current?.style.setProperty("--dx", `${d.dx}px`);
    face.current?.style.setProperty("--rot", `${d.dx / 25}deg`);
    // Selo "Manter"/"Esquecer" no card a partir de 40 px: diz o que soltar vai fazer.
    face.current?.setAttribute("data-swipe", d.dx > 40 ? "keep" : d.dx < -40 ? "forget" : "");
  };
  const onUp = () => {
    const d = drag.current;
    if (!d) return;
    face.current?.style.removeProperty("--dx");
    face.current?.style.removeProperty("--rot");
    face.current?.removeAttribute("data-swipe");
    if (Math.abs(d.dx) > 90) decide(d.dx > 0 ? "right" : "left");
    setTimeout(() => (drag.current = null)); // o clique que vem depois do arraste ainda vê o dx
  };
  const off = !!run.leaving;

  return (
    <div class="rd-session">
      <div class="rd-progress" role="progressbar" aria-label="Progresso da rodada" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress * 100)}>
        <span style={`width:${progress * 100}%`} />
      </div>
      <p class="sr-only" aria-live="polite">{`Card ${run.i + 1} de ${run.deck.length}: ${label}`}</p>
      <div class="rd-deck" style={ar ? `--ar:${ar}` : undefined}>
        <a
          key={c.id}
          ref={face}
          href={`#${encodeURIComponent(c.id)}`}
          class={`rd-face card${run.leaving ? ` is-leaving-${run.leaving}` : ""}`}
          data-kind={c.kind}
          aria-label={`Abrir ${label}`}
          draggable={false}
          onPointerDown={onDown}
          onPointerMove={onMove}
          onPointerUp={onUp}
          onPointerCancel={onUp}
          onClick={(e) => Math.abs(drag.current?.dx ?? 0) > 6 && e.preventDefault()}
        >
          <Frame card={c} />
          <Caption card={c} />
        </a>
        <ImageRetry card={c} name={label} />
        <button type="button" class="rd-orb is-forget" aria-disabled={off || undefined} aria-keyshortcuts="ArrowLeft" onClick={() => decide("left")}>
          <IconArrowLeft />
          <span>Esquecer</span>
        </button>
        <button type="button" class="rd-orb is-keep" aria-disabled={off || undefined} aria-keyshortcuts="ArrowRight" onClick={() => decide("right")}>
          <span>Manter</span>
          <IconArrowRight />
        </button>
      </div>
      <p class="rd-meta rd-keys" aria-hidden="true">
        <kbd>←</kbd> esquecer · clique para abrir · <kbd>→</kbd> manter
      </p>
    </div>
  );
}
